import { useCallback, useEffect, useRef } from 'react';

import { distance as levenshteinDistance } from 'fastest-levenshtein';

import { fg } from '@atlaskit/platform-feature-flags';

import { eventHub } from '../../pm-plugins/ai-event-hub/event-hub';

const ACTIONED_EVENT_DELAY = 10000;
const AI_GENERATED_TITLE_CHANGE_PERCENTAGE_THRESHOLD = 50;

export const useEditorAIReplacingTitle = ({
	enabled,
	title,
	handleNewTitle,
	canFireUnifiedAIAnalyticsEvent,
}: {
	canFireUnifiedAIAnalyticsEvent?: boolean;
	enabled: boolean;
	handleNewTitle: (title: string) => void;
	title: string;
}): void => {
	const canFireActionedEvent =
		canFireUnifiedAIAnalyticsEvent && fg('platform_editor_ai_suggest_title_actioned_event');

	const currentTitleRef = useRef<string>(title);
	useEffect(() => {
		currentTitleRef.current = title;
	}, [title]);

	const currentAnalyticsEventDataRef = useRef<{
		aiGeneratingTitle: string;
		fireUnifiedAnalyticsEvent: (eventType: 'actioned' | 'discarded') => void;
		isFinal: boolean;
	} | null>(null);

	const fireAnalyticEvent = useCallback(
		(currentTitle: string) => {
			if (!canFireActionedEvent) {
				return;
			}
			if (currentAnalyticsEventDataRef.current && currentAnalyticsEventDataRef.current.isFinal) {
				const titleChangePercentage = getTitleChangePercentage(
					currentAnalyticsEventDataRef.current.aiGeneratingTitle,
					currentTitle,
				);
				if (titleChangePercentage < AI_GENERATED_TITLE_CHANGE_PERCENTAGE_THRESHOLD) {
					currentAnalyticsEventDataRef.current.fireUnifiedAnalyticsEvent('actioned');
				} else {
					currentAnalyticsEventDataRef.current.fireUnifiedAnalyticsEvent('discarded');
				}

				// currentAnalyticsEventDataRef.current to null to avoid duplicate events.
				currentAnalyticsEventDataRef.current = null;
			}
		},
		[canFireActionedEvent],
	);

	const currentTimerIdRef = useRef<number | undefined>(undefined);
	const createTimerToFireActionedEvent = useCallback(() => {
		if (currentTimerIdRef.current) {
			window.clearTimeout(currentTimerIdRef.current);
		}
		currentTimerIdRef.current = window.setTimeout(() => {
			fireAnalyticEvent(currentTitleRef.current);
		}, ACTIONED_EVENT_DELAY);
	}, [fireAnalyticEvent]);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		const { unsubscribe } = eventHub.subscribe({
			event: 'replace title in confluence',
			callback: (data) => {
				const aiGeneratingTitle = data.title;

				if (canFireActionedEvent && data.isFinal !== undefined && data.fireUnifiedAnalyticsEvent) {
					/**
					 * This callback is called multiple times, for single replace title event
					 * 	because of streaming api.
					 *
					 * So if only currentTimerIdRef.current is not undefined,
					 * 	that means user has clicked on suggest title button again
					 * 	within ACTIONED_EVENT_DELAY.
					 *
					 * That means user didn't like previously generated title,
					 * 	so we will pass empty string, so that dismissed event is fired.
					 */
					if (currentTimerIdRef.current) {
						fireAnalyticEvent('');
						window.clearTimeout(currentTimerIdRef.current);
						currentTimerIdRef.current = undefined;
					}

					currentAnalyticsEventDataRef.current = {
						aiGeneratingTitle,
						isFinal: data.isFinal,
						fireUnifiedAnalyticsEvent: data.fireUnifiedAnalyticsEvent,
					};

					// When final stream arrives, we will set timer to fire actioned/dismissed event.
					if (data.isFinal) {
						createTimerToFireActionedEvent();
					}
				}

				handleNewTitle(aiGeneratingTitle);
			},
		});

		return () => {
			unsubscribe();
			window.clearTimeout(currentTimerIdRef.current);
		};
	}, [
		createTimerToFireActionedEvent,
		enabled,
		fireAnalyticEvent,
		handleNewTitle,
		canFireActionedEvent,
	]);
};

/**
 * Returns how much the AI-generated title was changed compared to the current title,
 * as a percentage (0–100). Based on Levenshtein distance normalised by the length
 * of the AI-generated title.
 *
 * @param aiGeneratedTitle - The title produced by AI
 * @param currentTitle - The title currently shown (e.g. before or after user edits)
 * @returns Change percentage from 0 (identical) to 100 (fully different). Returns 0 when aiGeneratedTitle is empty.
 */
export const getTitleChangePercentage = (
	aiGeneratedTitle: string,
	currentTitle: string,
): number => {
	if (aiGeneratedTitle.length === 0) {
		return 0;
	}
	const distance = levenshteinDistance(aiGeneratedTitle, currentTitle);
	return (distance / aiGeneratedTitle.length) * 100;
};
